// AskSurface.tsx — the /ask PEER SURFACE shell. Presentational: it receives the `ask` hook state + nav
// callbacks and re-houses AskThread/AskComposer (the Tailwind message column) in a full-page grid with a
// thread rail, header, and four states. All chrome geometry is token CSS (ask.css); no fetching here — the
// route wrapper (AskRoute in App.tsx) owns useAskQuery and the URL sync.
import { useEffect, useRef, useState, type DragEvent } from 'react';
import type { UseAskQuery, ChatSummary } from '../../hooks/useAskQuery';
import type { UseAskUploads } from '../../hooks/useAskUploads';
import { uploadFailureText } from '../../hooks/useAskUploads';
import { relativeTime } from '../../lib/formatting';
import { AskThread, AskComposer, type AttachmentChip } from './AskThread';
import { AskScopeBar } from './AskScopeBar';

interface AskSurfaceProps {
  ask: UseAskQuery;
  onNav: (id: string) => void;
  initialQuery: string | null; // ?q= prefill; sets the composer draft + focuses, NEVER auto-submits
  onNewChat: () => void; // navigate('/ask') — a fresh empty thread
  onSelectChat: (id: string) => void; // navigate('/ask/:id') — resume a thread from the rail
  corpusEmpty: boolean; // the team graph has no nodes yet → COLD state (no starter prompts)
  starters: string[]; // WARM starter prompts derived from the team's projects
  uploads: UseAskUploads; // the composer's file-attachment lane (U6/D32-lite)
}

const DAY = 86400000;

// Bucket the (server-sorted, newest-first) chat list into the rail's TODAY / PREVIOUS 7 DAYS / OLDER groups.
function bucketChats(chats: ChatSummary[], now: number): { label: string; rows: ChatSummary[] }[] {
  const today: ChatSummary[] = [];
  const week: ChatSummary[] = [];
  const older: ChatSummary[] = [];
  for (const c of chats) {
    const age = now - new Date(c.updated_at).getTime();
    if (age < DAY) today.push(c);
    else if (age < 7 * DAY) week.push(c);
    else older.push(c);
  }
  return [
    { label: 'Today', rows: today },
    { label: 'Previous 7 days', rows: week },
    { label: 'Older', rows: older },
  ].filter((g) => g.rows.length > 0);
}

export function AskSurface({
  ask,
  onNav,
  initialQuery,
  onNewChat,
  onSelectChat,
  corpusEmpty,
  starters,
  uploads,
}: AskSurfaceProps) {
  const [draft, setDraft] = useState(initialQuery ?? '');
  const [railOpen, setRailOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [dragging, setDragging] = useState(false);
  const dragDepthRef = useRef(0); // enter/leave fire per child; count depth so the overlay is stable
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true); // stick to the bottom UNLESS the user scrolled up to re-read
  const turnCountRef = useRef(0);
  const lastQRef = useRef<string | null>(null);

  // ?q prefill: set the draft + focus whenever a NEW query arrives (e.g. a second "Ask about this" click),
  // but never re-apply the same one on an unrelated re-render (which would clobber the user's edits), and
  // NEVER auto-submit — a 20-45s LLM turn must not fire from a URL.
  useEffect(() => {
    if (initialQuery != null && initialQuery !== lastQRef.current) {
      lastQRef.current = initialQuery;
      setDraft(initialQuery);
      composerRef.current?.focus();
    }
  }, [initialQuery]);

  // Keep the newest content in view while streaming, but only while pinned; a NEW turn always re-pins.
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (ask.turns.length > turnCountRef.current) pinnedRef.current = true;
    turnCountRef.current = ask.turns.length;
    if (pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [ask.turns]);

  const onThreadScroll = () => {
    const el = messagesRef.current;
    if (el) pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const submit = () => {
    const q = draft.trim();
    if (!q) return;
    // THE SEND GATE (bug-721, Teddy 2026-08-12): a question must not fire while an attachment is still
    // uploading/indexing — the engine only registers searchAttachments for READY uploads, so an early
    // send silently runs without the file. The composer disables its send button on the same flag; this
    // guard covers the Enter key path (both routes call submit()). Failed/rejected chips never block,
    // and a working chip is dismissable to lift the gate.
    if (uploads.indexing) return;
    // MESSAGE PLACEMENT (Teddy 2026-08-12): the sent question CARRIES its files — ready chips move
    // out of the composer onto this turn's bubble (and into the persist body's uploadIds, which is
    // what makes the placement durable); failed/rejected chips clear.
    const attachments = uploads.claimForSend();
    ask.ask(q, attachments.length ? { attachments } : undefined); // a new question replaces any stream still in flight (useAskQuery aborts it)
    setDraft('');
  };

  // The chips the composer renders, mapped from the hook (which owns the reason→text mapping so the view
  // needs no knowledge of the typed failure reasons).
  const attachmentChips: AttachmentChip[] = uploads.composerUploads.map((u) => ({
    uploadId: u.uploadId,
    filename: u.filename,
    mimeType: u.mimeType,
    byteSize: u.byteSize,
    status: u.status,
    failureText: uploadFailureText(u.failureReason),
    progress: u.progress,
  }));

  // The message-placement join (mig 171): a live turn already carries its optimistic `attachments`
  // from ask(); a persisted/reloaded turn re-derives them off the uploads list by its server id. The
  // spread keeps referential identity for turns with nothing to add.
  const turnsWithAttachments = ask.turns.map((t) =>
    t.attachments?.length || !t.id || !uploads.attachmentsByTurn[t.id] ? t : { ...t, attachments: uploads.attachmentsByTurn[t.id] },
  );
  // The props every AskComposer instance shares — one object so the four states cannot drift.
  const composerUploadProps = {
    onAttach: uploads.attachFiles,
    attachments: attachmentChips,
    onDismissAttachment: uploads.dismiss,
    indexing: uploads.indexing,
    attachError: uploads.attachError,
    attachDisabled: uploads.attaching,
  };

  // Drag-and-drop onto the whole surface (`.ask-root`), so a file dropped anywhere on Ask attaches. The
  // depth counter keeps the overlay from flickering as the drag crosses child boundaries. Only file drags
  // arm it — a text/selection drag is ignored.
  const isFileDrag = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  const onDragEnter = (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragging(true);
  };
  const onDragOver = (e: DragEvent) => {
    if (isFileDrag(e)) e.preventDefault(); // required for the drop to fire; also sets the copy cursor
  };
  const onDragLeave = (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragging(false);
  };
  const onDrop = (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) uploads.attachFiles(files);
  };

  // Encode each id: a source id can contain '#' (e.g. a Granola topic node '<note>#topic-k'), and a bare
  // '#' in a HashRouter URL truncates the param (react-router splits the hash before the query).
  // URLSearchParams.get on the read side (MapSurface) decodes it back.
  const showInMap = (ids: string[]) => onNav(`map?highlight=${ids.map(encodeURIComponent).join(',')}`);

  // The scope chip row — pinned chips are thread state (useAskQuery); inferred chips come off the
  // LAST turn's scope frame only, so they never outlive the answer that produced them (S4-3, §4.3).
  const lastFrame = ask.turns.length > 0 ? ask.turns[ask.turns.length - 1].scope : null;
  const scopeBar = (
    <AskScopeBar
      scope={ask.scope}
      setScope={ask.setScope}
      lastFrame={lastFrame}
      dismissedInferred={ask.dismissedInferred}
      dismissInferred={ask.dismissInferred}
      disabled={ask.streaming}
    />
  );

  const closeRail = () => setRailOpen(false);
  const selectChat = (id: string) => { onSelectChat(id); closeRail(); };
  const newChat = () => { onNewChat(); closeRail(); };
  // Deleting the ACTIVE chat resets the hook (deleteChat → newChat) but not the URL, which still names the
  // now-gone thread — so navigate to bare /ask, or the surface would sit on a thread that can never load.
  // AWAIT the delete first: navigating on a rejected request threw the user out of a thread that still
  // exists, and reported nothing.
  const removeChat = async (id: string) => {
    const wasActive = id === ask.activeChatId;
    setDeleteError('');
    try {
      await ask.deleteChat(id);
      if (wasActive) onNewChat();
    } catch {
      setDeleteError("Couldn't delete that thread. It's still here, so try again.");
    }
  };


  // The thread rail — shared by the desktop grid column and the ≤1100px off-canvas drawer.
  const groups = bucketChats(ask.chats, Date.now());
  const rail = (
    <aside className={`ask-rail${railOpen ? ' open' : ''}`}>
      <button className="ask-rail-new" onClick={newChat} aria-label="New question">＋ New chat</button>
      {deleteError && (
        <p role="alert" style={{ margin: '8px 12px 0', fontSize: 12, lineHeight: 1.4, color: 'var(--rose)' }}>
          {deleteError}
        </p>
      )}
      {groups.map((g) => (
        <div key={g.label}>
          <div className="ask-rail-group">{g.label}</div>
          {g.rows.map((c) => (
            <div key={c.id} className={`ask-rail-row${c.id === ask.activeChatId ? ' active' : ''}`}>
              <button className="ask-rail-row-main" onClick={() => selectChat(c.id)} title={c.title ?? 'Untitled chat'}>
                <div className="ask-rail-row-title">{c.title || 'Untitled chat'}</div>
                <div className="ask-rail-row-time">{relativeTime(c.updated_at)}</div>
              </button>
              <button className="ask-rail-del" onClick={() => removeChat(c.id)} aria-label="Delete chat" title="Delete chat">✕</button>
            </div>
          ))}
        </div>
      ))}
      <button className="ask-rail-foot" onClick={() => onNav('briefing')}>
        <b>Today's briefing</b> →
      </button>
    </aside>
  );

  // What fills the header/thread/composer column, by state. "Loading…" is driven by ask.loadingChat (a real
  // in-flight fetch) — NOT by an activeChatId-vs-URL mismatch, which also occurs for a brand-new local
  // thread (asked before its id is minted) and for a deleted thread, and would mask the live answer.
  let body: React.ReactNode;
  const notFound = ask.loadError === 'not_found';

  if (notFound) {
    body = (
      <div className="ask-notfound">
        <div className="ask-notfound-in">
          <div className="ask-notfound-title">This thread isn't on your account</div>
          <div className="ask-notfound-sub">
            A shared answer link only resumes for the person who asked it. Ask the question yourself below and
            it will be re-grounded in what you're entitled to see.
          </div>
          <div className="ask-empty-composer">
            <AskComposer value={draft} onChange={setDraft} onSubmit={submit} streaming={ask.streaming} onStop={ask.cancel} inputRef={composerRef} {...composerUploadProps} />
          </div>
        </div>
      </div>
    );
  } else if (!ask.hydrated || ask.loadingChat) {
    body = <div className="ask-loading" aria-live="polite">Loading…</div>;
  } else if (ask.turns.length === 0) {
    body = corpusEmpty ? (
      <div className="ask-empty">
        <div className="ask-empty-in">
          <div className="ask-empty-title">Your team's knowledge is still being distilled</div>
          <div className="ask-empty-sub">
            Answers are synthesized from your team's captured conversations, docs, PRs and meetings. The first
            answers appear within about ten minutes of connecting a source, or attach a file to ask about it now.
          </div>
          {/* Even with an empty team corpus a user can attach a file and ask about IT (U6) — so the cold
              state offers the composer, not only the connect-a-source CTA. */}
          <div className="ask-empty-composer">
            <AskComposer value={draft} onChange={setDraft} onSubmit={submit} streaming={ask.streaming} onStop={ask.cancel} inputRef={composerRef} {...composerUploadProps} />
          </div>
          <button className="ask-cold-cta" onClick={() => onNav('sources')}>Connect another source →</button>
        </div>
      </div>
    ) : (
      <div className="ask-empty">
        <div className="ask-empty-in">
          <div className="ask-empty-title">What do you want to know?</div>
          <div className="ask-empty-sub">
            Answers are synthesized from your team's captured conversations, docs, PRs and meetings, and every
            claim links back to its source.
          </div>
          <div className="ask-empty-composer">
            {scopeBar}
            <AskComposer value={draft} onChange={setDraft} onSubmit={submit} streaming={ask.streaming} onStop={ask.cancel} inputRef={composerRef} {...composerUploadProps} />
          </div>
          {starters.length > 0 && (
            <div className="ask-starters">
              {starters.map((p) => (
                <button key={p} className="ask-starter" onClick={() => setDraft(p)}>{p}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  } else {
    body = (
      <>
        <div className="ask-thread" ref={messagesRef} onScroll={onThreadScroll}>
          <div className="ask-thread-in">
            <AskThread turns={turnsWithAttachments} onNav={onNav} streaming={ask.streaming} onRegenerate={ask.regenerate} onShowInMap={showInMap} />
          </div>
        </div>
        <div className="ask-composer-dock">
          <div className="ask-composer-in">
            {scopeBar}
            <AskComposer value={draft} onChange={setDraft} onSubmit={submit} streaming={ask.streaming} onStop={ask.cancel} inputRef={composerRef} {...composerUploadProps} />
          </div>
          <div className="ask-composer-hint">Enter to send · Shift+Enter for a new line</div>
        </div>
      </>
    );
  }

  return (
    <div className="v2-content ov-surface ask-surface" data-streaming={ask.streaming ? '' : undefined}>
      <div
        className="ask-root"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragging && (
          <div className="ask-dropzone" aria-hidden="true">
            <div className="ask-dropzone-in">Drop a file to attach it to this chat</div>
          </div>
        )}
        {rail}
        <div className={`ask-rail-backdrop${railOpen ? ' open' : ''}`} onClick={closeRail} aria-hidden="true" />
        {/* No column header (redesign, no-title-on-title law): the masthead, composer placeholder,
            and question bubbles carry the surface's identity — only the rail toggle remains. */}
        <header className="ask-head">
          <div className="ask-head-titles" />
          <div className="ask-head-actions">
            <button className="ask-head-btn ask-head-threads" onClick={() => setRailOpen((v) => !v)} aria-label="Chat history">
              ☰ Threads
            </button>
          </div>
        </header>
        {ask.streaming && <div className="ask-hairline" aria-hidden="true" />}
        {body}
      </div>
    </div>
  );
}
